/**
 * 67.55: vehicles, voiced — the rules are `vehicleMix.ts`. One looping voice per audible vehicle, the loudest
 * VEHICLE_VOICES of them (as the drones' hum: `droneHum.ts`); each eases to this frame's heard values and fades out when
 * its vehicle falls silent or out of the loudest few. 67.67: each voice keeps its vehicle's drivetrain (`drivetrain.ts`),
 * stepped each update by the audio clock's time since the last — its revs, gear, load and shifts are the engine's (a
 * vehicle given a voice starts where a steady drive would sit). Synthesized (no files):
 *
 * - **engine**: the body oscillator at the firing pitch, a sawtooth an octave up, a sine sub an octave down, through a
 *   low-pass that opens with the load and the revs; the roughness is a gain swung by an oscillator at half the firing
 *   rate (a diesel's clatter); the intake and exhaust a band of noise that rises with the load. An electric motor is a
 *   sine whine and a triangle an octave under it, the hum, deeper and louder under load (driving or regen), with a faint
 *   hiss. 67.67: a two-stroke's Roots blower (the M113's) a sine whine at its multiple of the pitch, rising with the revs.
 * - **running gear**: noise through the ground's filter (vehicleMix ROLL), its loudness fluttering at the grit's rate —
 *   67.80: a steady wave, or random (low-passed noise) by the ground's `chatter`; the gravel's crunch, the high noise
 *   opened in pops by a thresholded slow noise (stone's).
 * - **tracks**: the links' clank — a band of noise gated by a narrow pulse at the links' rate; 67.67: their squeal, noise
 *   through a narrow band swung slowly (sprockets and idlers), loudest slow and turning.
 * - all of it through the air's low-pass, to its side, into the SFX bus (or a sheet's own destination).
 *
 * Cost: a voice is 3 oscillators, 3–4 noise sources (one shared buffer) and 3 modulators, about 32 nodes (67.80: +1 noise
 * source and 7 nodes for the random grit and the crunch); the M113's 36
 * (its blower, its squeal); six voices at most, ~150 nodes (216 were all six M113s; sound_sheets/vehicles.html). The
 * drivetrain is a few dozen flops a voice a frame. Presentation only.
 */
import { getAudioContext, getSfxBus, initAudio } from './sfx';
import { ENGINES, VEHICLE_VOICES, engineHeard, type VehicleHeard, type VehicleVoiceKind } from './vehicleMix';
import { GEARBOXES, gatherRate, settledDrivetrain, stepDrivetrain, type DrivetrainState, type GearboxSpec } from './drivetrain';

/** 67.55 (the owner: "we may need to dial in teh volume, not sure till we play"): × every vehicle's loudness, the Lab's Vehicle volume. */
export const VEHICLE_VOLUMES = ['0.5', '0.75', '1', '1.5'] as const;
let volumeOverride: number | null = null;
/** The Lab's launch sets it (`null` returns to the page's `?vehiclevolume=`, else 1). */
export function setVehicleVolume(v: number | null): void { volumeOverride = v; }
/** The vehicles' volume in force. */
export function vehicleVolume(): number {
  if (volumeOverride !== null) return volumeOverride;
  try {
    const q = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('vehiclevolume') : null;
    const v = q === null ? NaN : Number(q);
    return Number.isFinite(v) ? Math.max(0, Math.min(3, v)) : 1;
  } catch { return 1; }
}

interface Voice {
  kind: VehicleVoiceKind;
  out: GainNode; air: BiquadFilterNode; side: StereoPannerNode;
  body: OscillatorNode; octave: OscillatorNode; sub: OscillatorNode;
  engineGain: GainNode; engineFilter: BiquadFilterNode; roughLfo: OscillatorNode; roughDepth: GainNode;
  exhaustGain: GainNode;
  rollFilter: BiquadFilterNode; rollGain: GainNode; gritLfo: OscillatorNode;
  /** 67.80: the steady grit's depth, the random grit's (its low-pass), the crunch's level and its pops' rate */
  gritDepth: GainNode; gritRand: GainNode; gritLow: BiquadFilterNode; crunchGain: GainNode; crunchLow: BiquadFilterNode;
  clatterGain: GainNode; clatterLfo: OscillatorNode;
  subGain: GainNode; blowerOsc: OscillatorNode | null; blowerGain: GainNode | null; squealGain: GainNode | null;
  sources: AudioScheduledSourceNode[];
  /** 67.67: its vehicle's drivetrain, and the audio clock at its last step */
  train: DrivetrainState | null; at: number;
}

/** 67.80: the gravel's gate — nothing under `edge`, then opening fast: a slow noise's peaks become the crunch's pops. */
const gates = new WeakMap<BaseAudioContext, Float32Array<ArrayBuffer>>();
function gateCurve(ctx: BaseAudioContext): Float32Array<ArrayBuffer> {
  let c = gates.get(ctx);
  if (!c) {
    const n = 1024, edge = 0.5; c = new Float32Array(new ArrayBuffer(n * 4));
    for (let i = 0; i < n; i++) { const x = Math.abs((i / (n - 1)) * 2 - 1); c[i] = x <= edge ? 0 : Math.min(1, ((x - edge) / (1 - edge)) ** 1.5 * 1.6); }
    gates.set(ctx, c);
  }
  return c;
}

/** 67.67: the links' gate, a narrow pulse (a clank a link, not a square's buzz), one per context. */
const pulses = new WeakMap<BaseAudioContext, PeriodicWave>();
function pulseWave(ctx: BaseAudioContext): PeriodicWave | null {
  if (typeof ctx.createPeriodicWave !== 'function') return null;
  let w = pulses.get(ctx);
  if (!w) {
    const n = 24, re = new Float32Array(n), im = new Float32Array(n);
    for (let k = 1; k < n; k++) re[k] = Math.sin(Math.PI * k * 0.18) / (Math.PI * k) * (1 - k / n);   // an 18 % duty pulse, softened
    w = ctx.createPeriodicWave(re, im); pulses.set(ctx, w);
  }
  return w;
}

const noiseBufs = new WeakMap<BaseAudioContext, AudioBuffer>();
function noiseBuf(ctx: BaseAudioContext): AudioBuffer {
  let b = noiseBufs.get(ctx);
  if (!b) {
    const len = ctx.sampleRate * 2; b = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = b.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    noiseBufs.set(ctx, b);
  }
  return b;
}

/** Where the voices play: the game's SFX bus by default, or a sheet's (or an offline render's) own context and destination. */
export interface VehicleOut { ctx: BaseAudioContext; dest: AudioNode }

export class VehicleVoices {
  private readonly voices = new Map<number, Voice>();
  /** × every voice's loudness (the host sets it from `vehicleVolume()`; a sheet leaves it at 1). */
  volume = 1;
  /** 67.67: a kind's gearbox in place of its own (the sheet's electric two-speed dial). */
  gearbox: Partial<Record<VehicleVoiceKind, GearboxSpec>> = {};
  constructor(private readonly out?: VehicleOut) {}

  /** Voices live now (for the sheet's count). */
  get live(): number { return this.voices.size; }

  /** 67.67: a voiced vehicle's drivetrain now (the sheet's gear readout), or null. */
  drivetrainOf(id: number): DrivetrainState | null { return this.voices.get(id)?.train ?? null; }
  gearboxOf(kind: VehicleVoiceKind): GearboxSpec { return this.gearbox[kind] ?? GEARBOXES[kind]; }

  /** This frame's heard vehicles (id → kind and how it is heard); the loudest VEHICLE_VOICES keep a voice, the rest fade out. */
  update(heard: Array<{ id: number; kind: VehicleVoiceKind; h: VehicleHeard }>): void {
    const where = this.where();
    if (!where) return;
    const { ctx, dest } = where, t = ctx.currentTime;
    const loud = (x: VehicleHeard) => x.engine.gain + x.roll.gain + x.clatter.gain;
    const keep = heard.filter(x => loud(x.h) > 0.002).sort((a, b) => loud(b.h) - loud(a.h)).slice(0, VEHICLE_VOICES);
    const live = new Set(keep.map(k => k.id));
    for (const [id, v] of this.voices) if (!live.has(id)) { this.release(v, t); this.voices.delete(id); }
    for (const { id, kind, h } of keep) {
      let v = this.voices.get(id);
      if (v && v.kind !== kind) { this.release(v, t); v = undefined; }
      if (!v) { v = this.voice(ctx, dest, kind); v.at = t; this.voices.set(id, v); }
      // 67.67: its drivetrain, a step by the audio clock (its first frame where a steady drive sits)
      const g = this.gearboxOf(kind);
      v.train = v.train ? stepDrivetrain(v.train, g, h.drive, t - v.at, gatherRate(kind)) : settledDrivetrain(g, h.drive);
      v.at = t;
      this.set(v, h, t);
    }
  }

  stop(): void {
    const where = this.where();
    for (const v of this.voices.values()) this.release(v, where?.ctx.currentTime ?? 0);
    this.voices.clear();
  }

  private where(): VehicleOut | null {
    if (this.out) return this.out;
    initAudio();
    const ctx = getAudioContext(), bus = getSfxBus();
    return ctx && bus && ctx.state !== 'closed' ? { ctx, dest: bus } : null;
  }

  private set(v: Voice, h: VehicleHeard, t: number): void {
    const e = ENGINES[v.kind], k = 0.05;
    const eng = v.train ? engineHeard(v.kind, h.engine.near, h.drive.running, v.train) : h.engine;
    const p = Math.max(1, eng.pitch), push = Math.max(0, eng.load), rev = v.train?.rev ?? 0;
    v.body.frequency.setTargetAtTime(p, t, k);
    v.octave.frequency.setTargetAtTime(p * 2.01, t, k);
    v.sub.frequency.setTargetAtTime(p / 2, t, k);
    // the electric's hum: deeper and louder under load either way (driving, regen); an engine's sub as its spec
    v.subGain.gain.setTargetAtTime(e.electric ? 0.15 + 0.35 * Math.min(1, Math.abs(eng.load)) : e.sub, t, 0.1);
    v.roughLfo.frequency.setTargetAtTime(Math.max(1, p / 2), t, k);
    v.roughDepth.gain.setTargetAtTime(e.rough * 0.5 * (0.5 + push), t, 0.12);
    v.engineFilter.frequency.setTargetAtTime(e.electric ? 2500 + 2500 * Math.abs(eng.load) : 250 + 2300 * push + 500 * rev, t, 0.1);
    v.engineGain.gain.setTargetAtTime(eng.gain, t, 0.1);
    v.exhaustGain.gain.setTargetAtTime(eng.gain * e.noise * (0.3 + 0.7 * push), t, 0.1);
    if (v.blowerOsc && v.blowerGain) {
      v.blowerOsc.frequency.setTargetAtTime(p * (e.blower ?? 9), t, k);
      v.blowerGain.gain.setTargetAtTime(eng.gain * 0.05 * (0.25 + rev) * (0.6 + 0.4 * push), t, 0.1);
    }
    if (v.squealGain) v.squealGain.gain.setTargetAtTime(h.clatter.squeal, t, 0.2);
    v.rollFilter.type = h.roll.type; v.rollFilter.frequency.setTargetAtTime(h.roll.hz, t, 0.2); v.rollFilter.Q.value = h.roll.q;
    v.rollGain.gain.setTargetAtTime(h.roll.gain, t, 0.15);
    v.gritLfo.frequency.setTargetAtTime(Math.max(0.5, h.roll.grit), t, 0.2);
    v.gritDepth.gain.setTargetAtTime(0.3 * (1 - h.roll.chatter), t, 0.2);   // (67.80: the steady wave's share)
    v.gritRand.gain.setTargetAtTime(1.1 * h.roll.chatter, t, 0.2);          // (and the random grit's)
    v.gritLow.frequency.setTargetAtTime(Math.max(2, h.roll.grit), t, 0.2);
    v.crunchGain.gain.setTargetAtTime(h.roll.crunch, t, 0.15);
    v.crunchLow.frequency.setTargetAtTime(h.roll.crunchHz, t, 0.2);
    v.clatterGain.gain.setTargetAtTime(h.clatter.gain, t, 0.15);
    v.clatterLfo.frequency.setTargetAtTime(Math.max(0.5, h.clatter.rate), t, 0.15);
    v.air.frequency.setTargetAtTime(h.cutoff, t, 0.2);
    v.out.gain.setTargetAtTime(this.volume, t, 0.2);
    v.side.pan.setTargetAtTime(h.pan, t, 0.1);
  }

  private voice(ctx: BaseAudioContext, dest: AudioNode, kind: VehicleVoiceKind): Voice {
    const e = ENGINES[kind], sources: AudioScheduledSourceNode[] = [];
    const gain = (v: number) => { const g = ctx.createGain(); g.gain.value = v; return g; };
    const osc = (type: OscillatorType, hz: number) => { const o = ctx.createOscillator(); o.type = type; o.frequency.value = hz; sources.push(o); return o; };
    const noise = () => { const s = ctx.createBufferSource(); s.buffer = noiseBuf(ctx); s.loop = true; s.loopStart = Math.random(); sources.push(s); return s; };
    const out = gain(1);
    const air = ctx.createBiquadFilter(); air.type = 'lowpass'; air.frequency.value = 9000; air.Q.value = 0.5;
    const side = ctx.createStereoPanner();
    out.connect(air); air.connect(side); side.connect(dest);
    // the engine: body, octave, sub → a roughness gain swung at half the firing rate → the load's low-pass → its gain
    const engineGain = gain(0), engineFilter = ctx.createBiquadFilter(); engineFilter.type = 'lowpass'; engineFilter.Q.value = 0.8;
    const rough = gain(1), roughDepth = gain(0);
    const body = osc(e.body, e.idle || 300), octave = osc('sawtooth', (e.idle || 300) * 2), sub = osc(e.electric ? 'triangle' : 'sine', (e.idle || 300) / 2);
    const roughLfo = osc('sine', Math.max(1, e.idle / 2));
    const subGain = gain(e.electric ? 0.15 : e.sub);
    body.connect(gain(e.electric ? 0.5 : 0.55)).connect(rough);
    octave.connect(gain(e.electric ? 0 : 0.22)).connect(rough);
    sub.connect(subGain).connect(rough);
    // 67.67: a two-stroke's blower whine, past the roughness (a steady whine) into the load's low-pass
    let blowerOsc: OscillatorNode | null = null, blowerGain: GainNode | null = null;
    if (e.blower) { blowerOsc = osc('sine', (e.idle || 30) * e.blower); blowerGain = gain(0); blowerOsc.connect(blowerGain); blowerGain.connect(out); }
    roughLfo.connect(roughDepth); roughDepth.connect(rough.gain);
    rough.connect(engineFilter); engineFilter.connect(engineGain); engineGain.connect(out);
    // the intake and exhaust: a band of noise under the load
    const exhaust = noise(), exhaustBand = ctx.createBiquadFilter(); exhaustBand.type = 'bandpass'; exhaustBand.frequency.value = e.noiseHz; exhaustBand.Q.value = 0.9;
    const exhaustGain = gain(0);
    exhaust.connect(exhaustBand); exhaustBand.connect(exhaustGain); exhaustGain.connect(out);
    // the running gear: noise through the ground's filter, fluttering at its grit
    const roll = noise(), rollFilter = ctx.createBiquadFilter(), flutter = gain(0.7), gritDepth = gain(0.3), rollGain = gain(0);
    const gritLfo = osc('triangle', 8);
    roll.connect(rollFilter); rollFilter.connect(flutter); gritLfo.connect(gritDepth); gritDepth.connect(flutter.gain);
    flutter.connect(rollGain); rollGain.connect(out);
    // 67.80: the random grit (low-passed noise on the same flutter) and the gravel's crunch (high noise gated in pops)
    const rnd = noise(), gritLow = ctx.createBiquadFilter(), gritRand = gain(0);
    gritLow.type = 'lowpass'; gritLow.frequency.value = 20; gritLow.Q.value = 0.7;
    rnd.connect(gritLow); gritLow.connect(gritRand); gritRand.connect(flutter.gain);
    const crunchHi = ctx.createBiquadFilter(), crunchVca = gain(0), crunchGain = gain(0), crunchLow = ctx.createBiquadFilter(), crunchCtl = gain(11), crunchGate = ctx.createWaveShaper();
    crunchHi.type = 'highpass'; crunchHi.frequency.value = 1900; crunchLow.type = 'lowpass'; crunchLow.frequency.value = 30; crunchLow.Q.value = 0.7;
    crunchGate.curve = gateCurve(ctx);
    rnd.connect(crunchLow); crunchLow.connect(crunchCtl); crunchCtl.connect(crunchGate); crunchGate.connect(crunchVca.gain);
    roll.connect(crunchHi); crunchHi.connect(crunchVca); crunchVca.connect(crunchGain); crunchGain.connect(out);
    // the tracks: a band of noise gated at the links' rate (67.67: by a narrow pulse, a clank a link); their squeal, a
    // narrow band of the same noise swung slowly
    const clatterGain = gain(0), clatterLfo = osc('square', 6);
    let squealGain: GainNode | null = null;
    if (e.tracks) {
      const pulse = pulseWave(ctx);
      if (pulse) clatterLfo.setPeriodicWave(pulse);
      const links = noise(), band = ctx.createBiquadFilter(); band.type = 'bandpass'; band.frequency.value = 1100; band.Q.value = 1.6;
      const gate = gain(pulse ? 0.22 : 0.5), depth = gain(pulse ? 1 : 0.5);   // (the pulse, its mean taken out, dips to about −0.22: shut between links)
      links.connect(band); band.connect(gate); clatterLfo.connect(depth); depth.connect(gate.gain); gate.connect(clatterGain); clatterGain.connect(out);
      const squeal = ctx.createBiquadFilter(); squeal.type = 'bandpass'; squeal.frequency.value = 2650; squeal.Q.value = 18;
      const swing = gain(0.6), swingDepth = gain(0.4), swingLfo = osc('sine', 0.37);
      squealGain = gain(0);
      links.connect(squeal); squeal.connect(swing); swingLfo.connect(swingDepth); swingDepth.connect(swing.gain); swing.connect(squealGain); squealGain.connect(out);
    }
    for (const s of sources) s.start();
    return { kind, out, air, side, body, octave, sub, engineGain, engineFilter, roughLfo, roughDepth, exhaustGain, rollFilter, rollGain, gritLfo, gritDepth, gritRand, gritLow, crunchGain, crunchLow, clatterGain, clatterLfo, subGain, blowerOsc, blowerGain, squealGain, sources, train: null, at: 0 };
  }

  private release(v: Voice, t: number): void {
    v.out.gain.setTargetAtTime(0, t, 0.2);
    const end = t + 1.2;
    for (const s of v.sources) { try { s.stop(end); } catch { /* stopped */ } }
    if (typeof setTimeout === 'function') setTimeout(() => { try { v.side.disconnect(); } catch { /* gone */ } }, 1500);
  }
}
